import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasFreeAnonymousHints, getHintAuthMessage } from '../utils/anonymousHints';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { DndContext, useSensor, useSensors, MouseSensor, TouchSensor, closestCenter, useDraggable, useDroppable } from '@dnd-kit/core';
import { GameShell, useAiutiniModal } from './layout';
import { GameEndSection, LivesDisplay } from './game';
import { SerpentoniSkeleton } from './Skeleton';
import SnackAlert from './SnackAlert';
import Button from './Button';
import FaIcon from './FaIcon';
import GettonciniCostChip from './GettonciniCostChip';
import RewardedGettonciniCard from './RewardedGettonciniCard';
import { getGameConfig, getGameMessage } from '../games';
import { hasGameHistory } from '../util';
import { buildTrackedAuthRedirectUrl, logGameEvent, trackGameHintInsufficientBalance } from '../analytics';
import { useGettoncini } from '../contexts/GettonciniContext';
import { usePuzzleInstances } from '../contexts/PuzzleInstancesContext';
import { useAIHelper } from '../contexts/AIHelperContext';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import AIHelperTrigger from './ai-helper/AIHelperTrigger';
import styles from '../styles/Serpentoni.module.css';
import modalStyles from '../styles/modal.module.css';
import gameStyles from '../styles/GameStyles.module.css';
import { getDisabledHintCount, getDisabledHintsMessage } from '../utils/aiutiniModal';

const MAX_LIVES = 10;
const MAX_BOARD_HEIGHT_PX = 380;
const DRAG_CLICK_SUPPRESS_MS = 160;
const LOSS_REVEAL_STEP_MS = 1500;
const CELL_HIT_RADIUS_RATIO = 0.34;
const CELL_HIT_RADIUS_MIN_PX = 11;
const INSUFFICIENT_HINT_BALANCE_MESSAGE = 'Gettoncini insufficienti per questo aiutino.';

const canAffordHintCost = ({ cost, balance, isPreview = false, isFree = false }) => {
  if (isPreview || isFree) return true;
  if (typeof cost !== 'number' || typeof balance !== 'number') return true;
  return balance >= cost;
};

const ERROR_MESSAGES = {
  GAME_LOAD_ERROR: 'Errore nel caricamento del gioco. Riprova piu tardi.',
  NO_SELECTION: 'Seleziona almeno una lettera.',
  SUBMIT_ERROR: "Errore durante l'invio.",
  INVALID_WORD: 'Percorso non valido.',
  ALREADY_FOUND: 'Parola gia trovata.',
  HINT_NOT_AVAILABLE: 'Nessun aiutino disponibile.',
  HINT_ALREADY_USED: 'Hai gia usato questo aiutino.',
};

const miniPathFromIndexes = (indexes = []) => indexes
  .filter((pair) => Array.isArray(pair) && pair.length === 2)
  .map((pair) => ({ row: pair[0], col: pair[1] }))
  .filter((cell) => Number.isInteger(cell.row) && Number.isInteger(cell.col));

// Real puzzle data from backend/puzzles/serpentoni/2026-03-17.json
const MINI_DEMO_BOARD = [
  ['Q', 'C', 'A', 'I', 'A', 'U'],
  ['A', 'U', 'L', 'M', 'V', 'O'],
  ['S', 'A', 'L', 'E', 'O', 'C'],
  ['O', 'A', 'S', 'N', 'N', 'A'],
  ['I', 'L', 'A', 'T', 'O', 'B'],
  ['P', 'A', 'O', 'A', 'U', 'R'],
  ['N', 'E', 'I', 'R', 'B', 'R'],
];

const MINI_DEMO_WORD_PATHS = [
  miniPathFromIndexes([[0, 2], [0, 1], [0, 0], [1, 1], [1, 0]]),
  miniPathFromIndexes([[6, 4], [5, 4], [6, 5], [5, 5], [4, 4]]),
  miniPathFromIndexes([[2, 0], [3, 1], [2, 2], [3, 2], [4, 2]]),
  miniPathFromIndexes([[4, 5], [3, 5], [3, 4], [2, 5], [2, 4]]),
  miniPathFromIndexes([[5, 2], [4, 1], [4, 0], [3, 0]]),
  miniPathFromIndexes([[5, 0], [5, 1], [6, 0], [6, 1]]),
  miniPathFromIndexes([[0, 5], [1, 5], [1, 4], [0, 4]]),
];

const MINI_DEMO_SPANGRAM_PATH = miniPathFromIndexes(
  [[2, 1], [1, 2], [0, 3], [1, 3], [2, 3], [3, 3], [4, 3], [5, 3], [6, 3], [6, 2]],
);

const createMiniDemoTimeline = () => {
  const timeline = [];
  const push = (state, duration) => {
    timeline.push({ ...state, duration });
  };

  push({
    activePathType: null,
    activePathIndex: null,
    activeLength: 0,
    foundWordCount: 0,
    foundSpangram: false,
  }, 700);

  MINI_DEMO_WORD_PATHS.forEach((path, wordIndex) => {
    path.forEach((_, cellIndex) => {
      push({
        activePathType: 'word',
        activePathIndex: wordIndex,
        activeLength: cellIndex + 1,
        foundWordCount: wordIndex,
        foundSpangram: false,
      }, cellIndex === path.length - 1 ? 250 : 160);
    });

    push({
      activePathType: 'word',
      activePathIndex: wordIndex,
      activeLength: path.length,
      foundWordCount: wordIndex,
      foundSpangram: false,
    }, 320);

    push({
      activePathType: null,
      activePathIndex: null,
      activeLength: 0,
      foundWordCount: wordIndex + 1,
      foundSpangram: false,
    }, 360);
  });

  MINI_DEMO_SPANGRAM_PATH.forEach((_, cellIndex) => {
    push({
      activePathType: 'spangram',
      activePathIndex: null,
      activeLength: cellIndex + 1,
      foundWordCount: MINI_DEMO_WORD_PATHS.length,
      foundSpangram: false,
    }, cellIndex === MINI_DEMO_SPANGRAM_PATH.length - 1 ? 300 : 180);
  });

  push({
    activePathType: 'spangram',
    activePathIndex: null,
    activeLength: MINI_DEMO_SPANGRAM_PATH.length,
    foundWordCount: MINI_DEMO_WORD_PATHS.length,
    foundSpangram: false,
  }, 380);

  push({
    activePathType: null,
    activePathIndex: null,
    activeLength: 0,
    foundWordCount: MINI_DEMO_WORD_PATHS.length,
    foundSpangram: true,
  }, 3000);

  return timeline;
};

const MINI_DEMO_TIMELINE = createMiniDemoTimeline();

const MiniSerpentoniDemo = () => {
  const [phase, setPhase] = useState(0);
  const miniBoardRef = useRef(null);
  const miniCellRefs = useRef(new Map());
  const [miniBoardMetrics, setMiniBoardMetrics] = useState({ width: 0, height: 0 });
  const [miniLayoutVersion, setMiniLayoutVersion] = useState(0);

  useEffect(() => {
    const current = MINI_DEMO_TIMELINE[phase];
    const timeout = setTimeout(() => {
      setPhase((previous) => (previous + 1) % MINI_DEMO_TIMELINE.length);
    }, current?.duration || 700);

    return () => clearTimeout(timeout);
  }, [phase]);

  const boardHeight = MINI_DEMO_BOARD.length;
  const boardWidth = MINI_DEMO_BOARD[0].length;
  const phaseState = MINI_DEMO_TIMELINE[phase] || MINI_DEMO_TIMELINE[0];

  const setMiniCellRef = useCallback((row, col, element) => {
    const key = cellKey(row, col);
    if (!element) {
      miniCellRefs.current.delete(key);
      return;
    }
    miniCellRefs.current.set(key, element);
  }, []);

  const updateMiniBoardMetrics = useCallback(() => {
    if (!miniBoardRef.current) return;
    const rect = miniBoardRef.current.getBoundingClientRect();
    setMiniBoardMetrics({ width: rect.width, height: rect.height });
    setMiniLayoutVersion((value) => value + 1);
  }, []);

  useEffect(() => {
    updateMiniBoardMetrics();

    if (!miniBoardRef.current) return undefined;

    window.addEventListener('resize', updateMiniBoardMetrics);

    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        updateMiniBoardMetrics();
      });
      observer.observe(miniBoardRef.current);
    }

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', updateMiniBoardMetrics);
    };
  }, [boardHeight, boardWidth, updateMiniBoardMetrics]);

  const activePathTemplate = phaseState.activePathType === 'word'
    ? MINI_DEMO_WORD_PATHS[phaseState.activePathIndex] || []
    : phaseState.activePathType === 'spangram'
      ? MINI_DEMO_SPANGRAM_PATH
      : [];

  const activePath = activePathTemplate.slice(0, phaseState.activeLength || 0);
  const foundWordPaths = MINI_DEMO_WORD_PATHS.slice(0, phaseState.foundWordCount || 0);
  const foundSpangramPath = phaseState.foundSpangram ? MINI_DEMO_SPANGRAM_PATH : [];

  const miniCentersByKey = useMemo(() => {
    const next = {};
    if (!miniBoardRef.current) return next;

    const boardRect = miniBoardRef.current.getBoundingClientRect();
    miniCellRefs.current.forEach((element, key) => {
      if (!element) return;
      const rect = element.getBoundingClientRect();
      next[key] = {
        x: rect.left - boardRect.left + rect.width / 2,
        y: rect.top - boardRect.top + rect.height / 2,
      };
    });

    return next;
  }, [boardHeight, boardWidth, miniLayoutVersion]);

  const activePathSegments = useMemo(() => (
    buildPathSegments(activePath, miniCentersByKey).map((segment, index) => ({
      ...segment,
      key: `active-${phase}-${index}-${segment.key}`,
    }))
  ), [activePath, miniCentersByKey, phase]);

  const themePathSegments = useMemo(() => (
    foundWordPaths.flatMap((path, wordIndex) => (
      buildPathSegments(path, miniCentersByKey).map((segment, index) => ({
        ...segment,
        key: `word-${wordIndex}-${index}-${segment.key}`,
      }))
    ))
  ), [foundWordPaths, miniCentersByKey]);

  const spangramPathSegments = useMemo(() => (
    buildPathSegments(foundSpangramPath, miniCentersByKey).map((segment, index) => ({
      ...segment,
      key: `spangram-${index}-${segment.key}`,
    }))
  ), [foundSpangramPath, miniCentersByKey]);

  const activeKeys = new Set(activePath.map(({ row, col }) => cellKey(row, col)));
  const foundThemeKeys = new Set();
  foundWordPaths.forEach((path) => {
    path.forEach(({ row, col }) => foundThemeKeys.add(cellKey(row, col)));
  });
  const foundSpangramKeys = new Set(foundSpangramPath.map(({ row, col }) => cellKey(row, col)));

  const currentText = activePath.length > 0 ? buildTextFromPath(MINI_DEMO_BOARD, activePath) : '\u00A0';

  return (
    <div className={styles.miniSerpentoniDemo} aria-hidden="true">
      <div className={styles.miniSerpentoniSelection}>{currentText}</div>

      <div
        ref={miniBoardRef}
        className={styles.miniSerpentoniBoard}
        style={{
          gridTemplateColumns: `repeat(${boardWidth}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${boardHeight}, minmax(0, 1fr))`,
          aspectRatio: `${boardWidth} / ${boardHeight}`,
        }}
      >
        {miniBoardMetrics.width > 0
          && miniBoardMetrics.height > 0
          && (themePathSegments.length > 0
          || spangramPathSegments.length > 0
          || activePathSegments.length > 0) && (
          <svg
            className={styles.miniSerpentoniPathOverlay}
            viewBox={`0 0 ${miniBoardMetrics.width} ${miniBoardMetrics.height}`}
            preserveAspectRatio="none"
          >
            {themePathSegments.map((segment) => (
              <line
                key={segment.key}
                className={`${styles.miniSerpentoniPathSegment} ${styles.miniSerpentoniThemePathSegment}`}
                x1={segment.x1}
                y1={segment.y1}
                x2={segment.x2}
                y2={segment.y2}
              />
            ))}
            {spangramPathSegments.map((segment) => (
              <line
                key={segment.key}
                className={`${styles.miniSerpentoniPathSegment} ${styles.miniSerpentoniSpangramPathSegment}`}
                x1={segment.x1}
                y1={segment.y1}
                x2={segment.x2}
                y2={segment.y2}
              />
            ))}
            {activePathSegments.map((segment) => (
              <line
                key={segment.key}
                className={`${styles.miniSerpentoniPathSegment} ${styles.miniSerpentoniActivePathSegment}`}
                x1={segment.x1}
                y1={segment.y1}
                x2={segment.x2}
                y2={segment.y2}
              />
            ))}
          </svg>
        )}

        {MINI_DEMO_BOARD.map((rowLetters, rowIndex) => rowLetters.map((letter, colIndex) => {
          const key = cellKey(rowIndex, colIndex);
          const isThemeFound = foundThemeKeys.has(key);
          const isSpangramFound = foundSpangramKeys.has(key);
          const isActive = activeKeys.has(key);

          return (
            <span
              key={key}
              ref={(element) => setMiniCellRef(rowIndex, colIndex, element)}
              className={[
                styles.miniSerpentoniCell,
                isThemeFound && styles.miniSerpentoniCellFoundWord,
                isSpangramFound && styles.miniSerpentoniCellSpangram,
                isActive && styles.miniSerpentoniCellActive,
              ].filter(Boolean).join(' ')}
            >
              {letter}
            </span>
          );
        }))}
      </div>
    </div>
  );
};

const Instructions = () => (
  <div className={`${modalStyles.modalContent} ${modalStyles.instructions}`}>
    <p>Collega le lettere per trovare tutte le parole tema e lo strisciogramma.</p>
    <MiniSerpentoniDemo />
    <ul>
      <li>Puoi tracciare le parole trascinando con mouse/dito o cliccando su una lettera alla volta.</li>
      <li>Per correggere, torna indietro trascinando il mouse/dito o cliccando sulla lettera precedente; clicca su <strong>Annulla</strong> per cancellare completamente la tua selezione.</li>
      <li>Le parole hanno un numero minimo di 4 lettere.</li>
      <li>Il percorso di ogni parola non si incrocia mai, né con sé stesso né con il percorso di altre parole.</li>
      <li>Le parole tema trovate diventano verdi, mentre lo strisciogramma diventa dorato.</li>
      <li>Le lettere delle parole già trovate non sono piu selezionabili.</li>
      <li>Quando hai selezionato una parola, premi <strong>Invia</strong> per controllare.</li>
      <li>Se resti bloccato, usa gli aiutini <FaIcon className="fa fa-medkit" /> in alto a destra.</li>
    </ul>
  </div>
);

const Aiutini = ({
  onRevealWordToFind,
  onReveal,
  onRevealStarts,
  revealWordStartsUsed,
  disabled = false,
  loadingHint,
  hintCosts = {},
  hintAffordability = {},
  errorMessage = "",
  isSignedIn = true,
  hasFreeHints = false,
  onAuthRedirect = null,
  hasEverAuthenticated = false,
  isPreview = false,
  balance = 0,
}) => {
  const renderButtonContent = ({ alreadyUsed = false } = {}) => {
    if (isPreview) {
      return 'Ricevi aiutino';
    }
    if (!isSignedIn && !hasFreeHints) {
      return hasEverAuthenticated ? 'Accedi' : 'Registrati';
    }
    if (alreadyUsed) {
      return 'Già usato';
    }

    return 'Usa aiutino';
  };
  const shouldShowCostChip = (cost) => !isPreview && (hasFreeHints || (Number.isFinite(Number(cost)) && Number(cost) > 0));
  const disabledHintCount = getDisabledHintCount({
    isSignedIn,
    isPreview,
    hintAffordability,
    excludedHintKeys: revealWordStartsUsed ? ['reveal_word_starts'] : [],
  });
  const shouldShowInsufficientBalanceState = isSignedIn && (
    errorMessage === INSUFFICIENT_HINT_BALANCE_MESSAGE
    || disabledHintCount > 0
  );
  const shouldShowGenericError = Boolean(errorMessage) && !shouldShowInsufficientBalanceState;

  return (
    <div className={modalStyles.modalContent}>
      {shouldShowInsufficientBalanceState && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <div className={styles.aiutiniInlineErrorContent}>
            <div>
              {getDisabledHintsMessage({
                balance,
                disabledHintCount: Math.max(disabledHintCount, 1),
              })}
            </div>
            <div className={styles.aiutiniInlineErrorCard}>
              <RewardedGettonciniCard
                sourceSurface="aiutini_modal"
                gameSlug="serpentoni"
                compact
              />
            </div>
          </div>
        </div>
      )}
      {shouldShowGenericError && (
        <div className={styles.aiutiniInlineError} role="alert">
          <FaIcon className="fa-solid fa-circle-exclamation" />
          <span>
            {errorMessage}
          </span>
        </div>
      )}
      {(!isSignedIn || !shouldShowInsufficientBalanceState) && (
        <p className={styles.aiutiniPreamble}>
          {!isSignedIn && !isPreview && !hasFreeHints ? (
            <>
              Per usare gli aiutini hai bisogno di gettoncini, che puoi guadagnare con un account gratuito.{' '}
              <span className={styles.authLink} onClick={onAuthRedirect}>
                {hasEverAuthenticated ? 'Accedi' : 'Registrati'} <FaIcon className="fa-solid fa-arrow-right" />
              </span>
            </>
          ) : 'Se sei bloccato, puoi usare questi aiutini.'}
        </p>
      )}
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-location-dot" /></span>
            <span className={styles.aiutinoTitle}>Rivela iniziali</span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              className={[
                styles.aiutinoActionRight,
                styles.bubble,
                isSignedIn && !revealWordStartsUsed && !hintAffordability.reveal_word_starts
                  ? styles.aiutinoUnavailable
                  : '',
              ].filter(Boolean).join(' ')}
              onClick={onRevealStarts}
              disabled={disabled || loadingHint !== null || revealWordStartsUsed || (isSignedIn && !revealWordStartsUsed && !hintAffordability.reveal_word_starts)}
              loading={loadingHint === 'reveal_word_starts'}
              aria-disabled={disabled || (isSignedIn && !revealWordStartsUsed && !hintAffordability.reveal_word_starts)}
            >
              {renderButtonContent({ alreadyUsed: revealWordStartsUsed })}
            </Button>
            {shouldShowCostChip(hintCosts.reveal_word_starts ?? 10) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.reveal_word_starts ?? 10} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Evidenzia in blu la cella iniziale di ogni parola tema ancora mancante.</span>
      </div>
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-lightbulb" /></span>
            <span className={styles.aiutinoTitle}>Rivela lettere</span>
            <span className={styles.reusableBadge}><FaIcon className="fa-solid fa-rotate" /></span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              className={[
                styles.aiutinoActionRight,
                styles.bubble,
                isSignedIn && !hintAffordability.reveal_word_indexes
                  ? styles.aiutinoUnavailable
                  : '',
              ].filter(Boolean).join(' ')}
              onClick={onReveal}
              disabled={disabled || loadingHint !== null || (isSignedIn && !hintAffordability.reveal_word_indexes)}
              loading={loadingHint === 'reveal_word_indexes'}
              aria-disabled={disabled || (isSignedIn && !hintAffordability.reveal_word_indexes)}
            >
              {renderButtonContent()}
            </Button>
            {shouldShowCostChip(hintCosts.reveal_word_indexes) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.reveal_word_indexes} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Evidenzia le celle di una parola tema ancora mancante.</span>
      </div>
      <div className={styles.aiutinoRow}>
        <div className={styles.aiutinoHeaderRow}>
          <div className={styles.aiutinoHeaderTitle}>
            <span className={styles.aiutinoIcon}><FaIcon className="fa-solid fa-book-open" /></span>
            <span className={styles.aiutinoTitle}>Rivela parola</span>
            <span className={styles.reusableBadge}><FaIcon className="fa-solid fa-rotate" /></span>
          </div>
          <div className={styles.aiutinoActionWrap}>
            <Button
              secondary
              className={[
                styles.aiutinoActionRight,
                styles.bubble,
                isSignedIn && !hintAffordability.reveal_word_to_find
                  ? styles.aiutinoUnavailable
                  : '',
              ].filter(Boolean).join(' ')}
              onClick={onRevealWordToFind}
              disabled={disabled || loadingHint !== null || (isSignedIn && !hintAffordability.reveal_word_to_find)}
              loading={loadingHint === 'reveal_word_to_find'}
              aria-disabled={disabled || (isSignedIn && !hintAffordability.reveal_word_to_find)}
            >
              {renderButtonContent()}
            </Button>
            {shouldShowCostChip(hintCosts.reveal_word_to_find ?? 15) && (
              <GettonciniCostChip free={hasFreeHints} amount={hintCosts.reveal_word_to_find ?? 15} compact className={styles.aiutinoCostChip} />
            )}
          </div>
        </div>
        <span className={styles.aiutinoDesc}>Rivela una parola tema ancora mancante, trovala nella griglia e tracciala.</span>
      </div>
      <div className={styles.reusableHint}>
        <FaIcon className="fa-solid fa-rotate" />
        <span>Possono essere riutilizzati.</span>
      </div>
    </div>
  );
};

const SerpentoniSolutionsModal = ({ prompt, spangramText, themeWords = [] }) => (
  <div className={modalStyles.modalContent}>
    {prompt && (
      <div className={`${styles.promptStack} ${styles.soluzioniPromptStack}`}>
        <div className={`${styles.promptSplitRow} ${styles.promptSplitRowTop} ${styles.soluzioniPromptSplitRow}`}>
          <div className={`${styles.promptSplitLead} ${styles.promptSplitLeadPrimary}`}>
            <FaIcon className={`fa-solid fa-compass ${styles.promptSplitLeadIcon}`} aria-label="Tema" title="Tema" />
          </div>
          <div className={styles.promptSplitText}>
            <b>{prompt}</b>
          </div>
        </div>
      </div>
    )}
    <div className={styles.soluzioniList}>
      <div className={`${styles.soluzioniRow} ${styles.soluzioniSpangramRow}`}>
        <span className={styles.soluzioniLabel}>Strisciogramma</span>
        <span className={styles.soluzioniValue}>{spangramText}</span>
      </div>
      {themeWords.map((word, index) => (
        <div key={`${word}-${index}`} className={styles.soluzioniRow}>
          <span className={styles.soluzioniLabel}>{`Parola tema ${index + 1}`}</span>
          <span className={styles.soluzioniValue}>{word}</span>
        </div>
      ))}
    </div>
  </div>
);

const cellKey = (row, col) => `${row}:${col}`;

const isAdjacent = (a, b) => {
  const dr = Math.abs(a.row - b.row);
  const dc = Math.abs(a.col - b.col);
  return dr <= 1 && dc <= 1 && (dr !== 0 || dc !== 0);
};

const normalizePathFromIndexes = (indexes) => {
  if (!Array.isArray(indexes)) return [];
  return indexes
    .filter((pair) => Array.isArray(pair) && pair.length === 2)
    .map((pair) => ({ row: pair[0], col: pair[1] }))
    .filter((cell) => Number.isInteger(cell.row) && Number.isInteger(cell.col));
};

const buildTextFromPath = (board, path) => path
  .map(({ row, col }) => board?.[row]?.[col] || '')
  .join('')
  .toUpperCase();

const serializePath = (path) => (
  Array.isArray(path)
    ? path.map(({ row, col }) => `${row}:${col}`).join('|')
    : ''
);

const arePathsEqual = (left, right) => {
  if (left === right) return true;
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;

  for (let index = 0; index < left.length; index += 1) {
    const leftCell = left[index];
    const rightCell = right[index];
    if (!leftCell || !rightCell) return false;
    if (leftCell.row !== rightCell.row || leftCell.col !== rightCell.col) return false;
  }

  return true;
};

const areWordPathMapsEqual = (left, right) => {
  if (left === right) return true;
  if (!left || !right || typeof left !== 'object' || typeof right !== 'object') return false;

  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  if (leftKeys.length !== rightKeys.length) return false;

  for (let index = 0; index < leftKeys.length; index += 1) {
    if (leftKeys[index] !== rightKeys[index]) return false;
    if (!arePathsEqual(left[leftKeys[index]], right[rightKeys[index]])) return false;
  }

  return true;
};

const getAiLookupKey = (component, word) => {
  if (!component || !word) return '';
  return `${component}:${String(word).toLowerCase()}`;
};

const buildPathSegments = (path, centersByKey) => {
  if (!Array.isArray(path) || path.length < 2) return [];

  const segments = [];

  for (let index = 0; index < path.length - 1; index += 1) {
    const from = path[index];
    const to = path[index + 1];

    const fromPoint = centersByKey[cellKey(from.row, from.col)];
    const toPoint = centersByKey[cellKey(to.row, to.col)];

    if (!fromPoint || !toPoint) continue;

    segments.push({
      key: `${from.row}:${from.col}-${to.row}:${to.col}-${index}`,
      x1: fromPoint.x,
      y1: fromPoint.y,
      x2: toPoint.x,
      y2: toPoint.y,
    });
  }

  return segments;
};

const SerpentoniCell = ({
  cellId,
  row,
  col,
  letter,
  className,
  onClick,
  setCellRef,
  isInteractionDisabled,
  isDragDisabled,
  allowAiSelection,
  aiComponent,
  aiWord,
  onHoverStart,
  onHoverEnd,
  isPressed,
}) => {
  const { attributes, listeners, setNodeRef: setDragRef } = useDraggable({
    id: `cell-drag-${cellId}`,
    data: { row, col, key: cellId },
    disabled: isDragDisabled,
  });
  const { setNodeRef: setDropRef } = useDroppable({
    id: `cell-drop-${cellId}`,
    data: { row, col, key: cellId },
  });

  const setNodeRef = useCallback((element) => {
    setDragRef(element);
    setDropRef(element);
    setCellRef(row, col, element);
  }, [col, row, setCellRef, setDragRef, setDropRef]);

  return (
    <button
      type="button"
      ref={setNodeRef}
      className={[className].filter(Boolean).join(' ')}
      onClick={onClick}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
      onFocus={onHoverStart}
      onBlur={onHoverEnd}
      disabled={isInteractionDisabled && !allowAiSelection}
      aria-label={`Lettera ${letter}`}
      aria-pressed={isPressed}
      {...(aiComponent && aiWord
        ? { 'data-ai-component': aiComponent, 'data-ai-word': aiWord, 'data-ai-skip-highlight': 'true' }
        : {})}
      {...attributes}
      {...listeners}
    >
      <span>{letter}</span>
    </button>
  );
};

const SerpentoniGame = ({
  date,
  gameConfig: providedGameConfig,
  puzzleConfig,
  sessionState,
  solutions,
  gameOver,
  won,
  justWon,
  justLost = false,
  isLoading,
  sessionError,
  isEventPending,
  dispatchEvent,
  dispatchHintEvent,
  hintAllowance = null,
  refreshHintAllowance = null,
  isSignedIn = false,
  isPreview = false,
  previewSponsorship = null,
  showNavigation = true,
  showGameHeader = true,
  previewEndLabel,
}) => {
  const hasFreeHints = hasFreeAnonymousHints(hintAllowance, 'serpentoni', isSignedIn);
  const navigate = useNavigate();
  const location = useLocation();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const { selection } = useAIHelper();

  const gameConfig = providedGameConfig || getGameConfig('serpentoni');
  const effectiveDate = date || 'preview';
  const shouldTrack = !isPreview;

  const { balance, getHintCost } = useGettoncini();
  const { getPuzzleInstanceId, getPuzzleInstance } = usePuzzleInstances();
  const puzzleInstanceId = getPuzzleInstanceId(gameConfig.id, effectiveDate);
  const sponsorship = previewSponsorship || (isPreview ? null : getPuzzleInstance(gameConfig.id, effectiveDate)?.sponsorship || null);

  const {
    isModalOpen,
    modalTitle,
    modalContent,
    closeModal,
    showModal,
    showAiutiniModal,
    refreshAiutiniModal,
    isAiutiniModalOpen,
  } = useAiutiniModal();

  useEffect(() => {
    if (isAiutiniModalOpen) refreshHintAllowance?.();
  }, [isAiutiniModalOpen, refreshHintAllowance]);

  const snackAlertRef = useRef(null);
  const hasShownInstructionsRef = useRef(false);
  const gameplayControlsRef = useRef(null);

  const boardRef = useRef(null);
  const cellRefs = useRef(new Map());

  const [boardMetrics, setBoardMetrics] = useState({ width: 0, height: 0 });
  const [layoutVersion, setLayoutVersion] = useState(0);

  const [currentPath, setCurrentPath] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingHint, setLoadingHint] = useState(null);
  const [aiutiniError, setAiutiniError] = useState('');
  const [endSectionAnchor, setEndSectionAnchor] = useState(null);

  const [invalidCellKeys, setInvalidCellKeys] = useState(new Set());
  const invalidFeedbackTimeoutRef = useRef(null);

  const [foundWordPathsByIndex, setFoundWordPathsByIndex] = useState({});
  const [foundSpangramPath, setFoundSpangramPath] = useState([]);
  const [hintedCellKeys, setHintedCellKeys] = useState(new Set());
  const [showEndContent, setShowEndContent] = useState(false);
  const [lossRevealedWordIndices, setLossRevealedWordIndices] = useState(new Set());
  const [lossSpangramRevealed, setLossSpangramRevealed] = useState(false);
  const [lossRevealCurrentText, setLossRevealCurrentText] = useState('');
  const [lossRevealSettled, setLossRevealSettled] = useState(false);
  const [hoveredAiLookupKey, setHoveredAiLookupKey] = useState('');
  const lastDragOverKeyRef = useRef(null);
  const suppressClickUntilRef = useRef(0);
  const lossRevealTimeoutsRef = useRef([]);
  const lossRevealScheduledRef = useRef(false);

  const lives = typeof sessionState?.lives === 'number' ? sessionState.lives : MAX_LIVES;
  const foundWordIndices = useMemo(() => (
    Array.isArray(sessionState?.found_word_indices)
      ? Array.from(new Set(sessionState.found_word_indices)).filter(Number.isInteger)
      : []
  ), [sessionState?.found_word_indices]);
  const spangramFound = !!sessionState?.spangram_found;
  const sessionFoundWordPathsByIndex = useMemo(() => {
    const raw =
      sessionState?.found_word_paths_by_index
      || sessionState?.foundWordPathsByIndex
      || {};
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
      return {};
    }

    const next = {};
    Object.entries(raw).forEach(([rawIndex, rawPath]) => {
      const wordIndex = Number(rawIndex);
      if (!Number.isInteger(wordIndex)) return;
      const normalizedPath = normalizePathFromIndexes(rawPath);
      if (normalizedPath.length === 0) return;
      next[wordIndex] = normalizedPath;
    });
    return next;
  }, [sessionState?.found_word_paths_by_index, sessionState?.foundWordPathsByIndex]);
  const sessionFoundSpangramPath = useMemo(
    () => normalizePathFromIndexes(
      sessionState?.found_spangram_indexes
      || sessionState?.foundSpangramIndexes
      || [],
    ),
    [sessionState?.found_spangram_indexes, sessionState?.foundSpangramIndexes],
  );
  const sessionRevealedWordStartIndexes = useMemo(
    () => normalizePathFromIndexes(
      sessionState?.revealed_word_start_indexes
      || sessionState?.revealedWordStartIndexes
      || [],
    ),
    [sessionState?.revealed_word_start_indexes, sessionState?.revealedWordStartIndexes],
  );
  const revealWordStartsUsed = !!(
    sessionState?.reveal_word_starts_used
    || sessionState?.revealWordStartsUsed
  );
  const revealedWordToFindText = useMemo(() => {
    const raw = (
      sessionState?.revealed_word_to_find_text
      || sessionState?.revealedWordToFindText
      || ''
    );
    return typeof raw === 'string' ? raw.toUpperCase().trim() : '';
  }, [sessionState?.revealedWordToFindText, sessionState?.revealed_word_to_find_text]);

  const board = Array.isArray(puzzleConfig?.board) ? puzzleConfig.board : [];
  const boardHeight = puzzleConfig?.height || board.length || 0;
  const boardWidth = puzzleConfig?.width || (board[0]?.length ?? 0);
  const boardMaxWidth = boardHeight > 0
    ? `${(MAX_BOARD_HEIGHT_PX * boardWidth) / boardHeight}px`
    : '100%';
  const totalWords = Number(puzzleConfig?.total_words);
  const themeWordsTotal = Number.isInteger(totalWords) && totalWords >= 0 ? totalWords : 0;
  const foundThemeWords = Math.min(foundWordIndices.length, themeWordsTotal);
  const remainingWordCount = Math.max(0, themeWordsTotal - foundThemeWords);
  const remainingWordsText = (spangramFound ? '' : 'Strisciogramma, ')
    + String(remainingWordCount)
    + ' '
    + (remainingWordCount === 1 ? 'parola' : 'parole');

  const solutionBundle = solutions?.[0] || null;
  const solutionsPrompt = useMemo(() => {
    if (typeof solutionBundle?.prompt === 'string' && solutionBundle.prompt.trim()) {
      return solutionBundle.prompt.trim();
    }

    if (typeof puzzleConfig?.prompt === 'string' && puzzleConfig.prompt.trim()) {
      return puzzleConfig.prompt.trim();
    }

    return '';
  }, [puzzleConfig?.prompt, solutionBundle?.prompt]);
  const solutionsSpangramText = useMemo(() => {
    if (!solutionBundle) return '';

    const fallbackText = buildTextFromPath(
      board,
      normalizePathFromIndexes(solutionBundle?.spangram?.indexes),
    ).trim();
    if (typeof solutionBundle?.spangram?.text === 'string') {
      return solutionBundle.spangram.text.toUpperCase().trim() || fallbackText;
    }
    return fallbackText;
  }, [board, solutionBundle]);
  const solutionsThemeWords = useMemo(() => {
    if (!Array.isArray(solutionBundle?.words)) return [];

    return solutionBundle.words
      .map((entry) => {
        const fallbackText = buildTextFromPath(
          board,
          normalizePathFromIndexes(entry?.indexes),
        ).trim();
        if (typeof entry?.text === 'string') {
          return entry.text.toUpperCase().trim() || fallbackText;
        }
        return fallbackText;
      })
      .filter(Boolean);
  }, [board, solutionBundle]);
  const canOpenSolutionsModal = (
    gameOver
    && !!solutionBundle
    && !!solutionsPrompt
    && !!solutionsSpangramText
    && solutionsThemeWords.length > 0
  );

  const solvedWordPaths = useMemo(() => {
    if (!solutionBundle?.words) return [];
    return solutionBundle.words
      .map((entry) => normalizePathFromIndexes(entry.indexes))
      .filter((path) => path.length > 0);
  }, [solutionBundle]);

  const solvedSpangramPath = useMemo(() => {
    if (!solutionBundle?.spangram) return [];
    return normalizePathFromIndexes(solutionBundle.spangram.indexes);
  }, [solutionBundle]);

  const lossRevealQueue = useMemo(() => {
    if (!solutionBundle) return [];

    const alreadyFoundWordIndices = new Set(foundWordIndices);
    const queue = [];

    (solutionBundle.words || []).forEach((entry, index) => {
      if (alreadyFoundWordIndices.has(index)) return;

      const fallbackText = buildTextFromPath(board, normalizePathFromIndexes(entry?.indexes));
      const displayText = typeof entry?.text === 'string'
        ? entry.text.toUpperCase().trim() || fallbackText
        : fallbackText;

      queue.push({
        type: 'word',
        wordIndex: index,
        displayText,
      });
    });

    if (!spangramFound && solutionBundle.spangram) {
      const fallbackText = buildTextFromPath(
        board,
        normalizePathFromIndexes(solutionBundle.spangram.indexes),
      );
      const displayText = typeof solutionBundle.spangram.text === 'string'
        ? solutionBundle.spangram.text.toUpperCase().trim() || fallbackText
        : fallbackText;

      queue.push({
        type: 'spangram',
        wordIndex: null,
        displayText,
      });
    }

    return queue;
  }, [board, foundWordIndices, solutionBundle, spangramFound]);

  const effectiveWordPaths = useMemo(() => {
    if (!gameOver) {
      return Object.values(foundWordPathsByIndex);
    }

    if (solvedWordPaths.length === 0) {
      return Object.values(foundWordPathsByIndex);
    }

    if (won) {
      return solvedWordPaths;
    }

    if (!justLost) {
      return solvedWordPaths;
    }

    const indicesToShow = new Set(foundWordIndices);
    lossRevealedWordIndices.forEach((index) => indicesToShow.add(index));

    return Array.from(indicesToShow)
      .sort((a, b) => a - b)
      .map((index) => solvedWordPaths[index])
      .filter((path) => Array.isArray(path) && path.length > 0);
  }, [
    foundWordIndices,
    foundWordPathsByIndex,
    gameOver,
    justLost,
    lossRevealedWordIndices,
    solvedWordPaths,
    won,
  ]);

  const effectiveSpangramPath = useMemo(() => {
    if (!gameOver) {
      return foundSpangramPath;
    }

    if (solvedSpangramPath.length === 0) {
      return foundSpangramPath;
    }

    if (!justLost || won || spangramFound || lossSpangramRevealed) {
      return solvedSpangramPath;
    }

    return [];
  }, [
    foundSpangramPath,
    gameOver,
    justLost,
    lossSpangramRevealed,
    solvedSpangramPath,
    spangramFound,
    won,
  ]);

  const foundWordCellKeys = useMemo(() => {
    const next = new Set();
    effectiveWordPaths.forEach((path) => {
      path.forEach(({ row, col }) => next.add(cellKey(row, col)));
    });
    return next;
  }, [effectiveWordPaths]);

  const spangramCellKeys = useMemo(() => {
    const next = new Set();
    effectiveSpangramPath.forEach(({ row, col }) => next.add(cellKey(row, col)));
    return next;
  }, [effectiveSpangramPath]);

  const lockedCellKeys = useMemo(() => {
    const next = new Set(foundWordCellKeys);
    spangramCellKeys.forEach((key) => next.add(key));
    return next;
  }, [foundWordCellKeys, spangramCellKeys]);

  const currentPathKeys = useMemo(() => {
    const next = new Set();
    currentPath.forEach(({ row, col }) => next.add(cellKey(row, col)));
    return next;
  }, [currentPath]);
  const revealedWordStartCellKeys = useMemo(() => {
    const next = new Set();
    sessionRevealedWordStartIndexes.forEach(({ row, col }) => next.add(cellKey(row, col)));
    return next;
  }, [sessionRevealedWordStartIndexes]);

  const currentPathText = useMemo(
    () => (currentPath.length > 0 ? buildTextFromPath(board, currentPath) : ''),
    [board, currentPath],
  );
  const currentSelectionChars = useMemo(() => {
    if (!revealedWordToFindText) return null;

    const hintedChars = Array.from(revealedWordToFindText);
    const selectedChars = Array.from(currentPathText);
    const merged = hintedChars.map((hintedChar, index) => {
      const selectedChar = selectedChars[index];
      if (selectedChar) {
        return { value: selectedChar, isHint: false };
      }
      return { value: hintedChar, isHint: true };
    });

    if (selectedChars.length > hintedChars.length) {
      selectedChars.slice(hintedChars.length).forEach((char) => {
        merged.push({ value: char, isHint: false });
      });
    }

    return merged;
  }, [currentPathText, revealedWordToFindText]);

  const backtrackTargetKey = useMemo(() => {
    if (currentPath.length < 2) return null;
    const prev = currentPath[currentPath.length - 2];
    return cellKey(prev.row, prev.col);
  }, [currentPath]);

  const currentTipKey = useMemo(() => {
    if (currentPath.length === 0) return null;
    const tip = currentPath[currentPath.length - 1];
    return cellKey(tip.row, tip.col);
  }, [currentPath]);

  const aiSelectionEnabled = gameOver && !isPreview;
  const shouldAnimateLossReveal = gameOver && !won && justLost;
  const missingWordCount = Math.max(0, solvedWordPaths.length - foundWordIndices.length);
  const isLossWordRevealComplete = lossRevealedWordIndices.size >= missingWordCount;
  const isLossSpangramRevealComplete =
    spangramFound || solvedSpangramPath.length === 0 || lossSpangramRevealed;
  const isLossRevealComplete = !shouldAnimateLossReveal
    || (isLossWordRevealComplete && isLossSpangramRevealComplete && lossRevealSettled);
  const isAIOnboardingReady =
    aiSelectionEnabled && showEndContent && (won || isLossRevealComplete);
  const selectedAiLookupKey = useMemo(() => {
    if (!aiSelectionEnabled || !selection) return '';
    const component = selection.component;
    if (component !== 'serpentoni_word' && component !== 'serpentoni_spangram') {
      return '';
    }
    return getAiLookupKey(component, selection.word);
  }, [aiSelectionEnabled, selection]);
  const isSubmitPending = isSubmitting || (isEventPending && loadingHint === null);
  const isInteractionDisabled = gameOver || isLoading || isSubmitPending;

  const hintCosts = useMemo(() => ({
    reveal_word_to_find: getHintCost('serpentoni', 'reveal_word_to_find'),
    reveal_word_indexes: getHintCost('serpentoni', 'reveal_word_indexes'),
    reveal_word_starts: getHintCost('serpentoni', 'reveal_word_starts'),
  }), [getHintCost]);
  const revealWordToFindCost = hintCosts.reveal_word_to_find ?? 15;
  const revealWordIndexesCost = hintCosts.reveal_word_indexes;
  const revealWordStartsCost = hintCosts.reveal_word_starts ?? 10;
  const aiutiniHintCosts = useMemo(() => ({
    reveal_word_to_find: revealWordToFindCost,
    reveal_word_indexes: revealWordIndexesCost,
    reveal_word_starts: revealWordStartsCost,
  }), [revealWordIndexesCost, revealWordStartsCost, revealWordToFindCost]);
  const hintAffordability = useMemo(() => ({
    reveal_word_to_find: canAffordHintCost({ cost: revealWordToFindCost, balance, isPreview, isFree: hasFreeHints }),
    reveal_word_indexes: canAffordHintCost({ cost: revealWordIndexesCost, balance, isPreview, isFree: hasFreeHints }),
    reveal_word_starts: canAffordHintCost({ cost: revealWordStartsCost, balance, isPreview, isFree: hasFreeHints }),
  }), [balance, isPreview, revealWordIndexesCost, revealWordStartsCost, revealWordToFindCost, hasFreeHints]);

  const setCellRef = useCallback((row, col, element) => {
    const key = cellKey(row, col);
    if (!element) {
      cellRefs.current.delete(key);
      return;
    }
    cellRefs.current.set(key, element);
  }, []);

  const updateBoardMetrics = useCallback(() => {
    if (!boardRef.current) return;
    const rect = boardRef.current.getBoundingClientRect();
    setBoardMetrics({ width: rect.width, height: rect.height });
    setLayoutVersion((value) => value + 1);
  }, []);

  const clearLossRevealTimeouts = useCallback(() => {
    lossRevealTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    lossRevealTimeoutsRef.current = [];
  }, []);
  const resetLossRevealState = useCallback(() => {
    setLossRevealedWordIndices((previous) => (previous.size === 0 ? previous : new Set()));
    setLossSpangramRevealed((previous) => (previous ? false : previous));
    setLossRevealCurrentText((previous) => (previous ? '' : previous));
    setLossRevealSettled(false);
  }, []);

  useEffect(() => {
    updateBoardMetrics();

    if (!boardRef.current) return undefined;
    window.addEventListener('resize', updateBoardMetrics);

    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        updateBoardMetrics();
      });
      observer.observe(boardRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener('resize', updateBoardMetrics);
    };
  }, [boardHeight, boardWidth, updateBoardMetrics]);

  useEffect(() => {
    if (gameOver && !showEndContent) {
      setShowEndContent(true);
    }
  }, [gameOver, showEndContent]);

  useEffect(() => () => {
    if (invalidFeedbackTimeoutRef.current) {
      clearTimeout(invalidFeedbackTimeoutRef.current);
    }
  }, []);

  useEffect(() => () => {
    clearLossRevealTimeouts();
  }, [clearLossRevealTimeouts]);

  useEffect(() => {
    if (isPreview || isLoading) return;

    if (!hasShownInstructionsRef.current && !hasGameHistory(gameConfig.id)) {
      hasShownInstructionsRef.current = true;
      showModal({
        title: 'Istruzioni',
        content: <Instructions />,
      });

      if (shouldTrack) {
        logGameEvent('instructions_viewed', gameConfig.id, effectiveDate, { first_time: true });
      }
    }
  }, [effectiveDate, gameConfig.id, isLoading, isPreview, shouldTrack, showModal]);

  useEffect(() => {
    setFoundWordPathsByIndex((previous) => (
      areWordPathMapsEqual(previous, sessionFoundWordPathsByIndex)
        ? previous
        : sessionFoundWordPathsByIndex
    ));
  }, [sessionFoundWordPathsByIndex]);

  useEffect(() => {
    setFoundSpangramPath((previous) => (
      arePathsEqual(previous, sessionFoundSpangramPath)
        ? previous
        : sessionFoundSpangramPath
    ));
  }, [sessionFoundSpangramPath]);

  useEffect(() => {
    if (!shouldAnimateLossReveal) {
      clearLossRevealTimeouts();
      lossRevealScheduledRef.current = false;
      resetLossRevealState();
      return;
    }

    if (lossRevealScheduledRef.current) return;
    if (solvedWordPaths.length === 0 && solvedSpangramPath.length === 0) return;

    lossRevealScheduledRef.current = true;
    resetLossRevealState();

    lossRevealQueue.forEach((entry, revealOrder) => {
      const timeoutId = setTimeout(() => {
        if (entry.type === 'word' && Number.isInteger(entry.wordIndex)) {
          setLossRevealedWordIndices((previous) => {
            const next = new Set(previous);
            next.add(entry.wordIndex);
            return next;
          });
        } else if (entry.type === 'spangram') {
          setLossSpangramRevealed(true);
        }

        setLossRevealCurrentText(entry.displayText || '');
      }, (revealOrder + 1) * LOSS_REVEAL_STEP_MS);
      lossRevealTimeoutsRef.current.push(timeoutId);
    });

    if (lossRevealQueue.length > 0) {
      const timeoutId = setTimeout(() => {
        setLossRevealCurrentText('');
        setLossRevealSettled(true);
      }, (lossRevealQueue.length + 1) * LOSS_REVEAL_STEP_MS);
      lossRevealTimeoutsRef.current.push(timeoutId);
    } else {
      setLossRevealSettled(true);
    }
  }, [
    clearLossRevealTimeouts,
    lossRevealQueue,
    resetLossRevealState,
    shouldAnimateLossReveal,
    solvedSpangramPath,
    solvedWordPaths,
  ]);

  const redirectToAuth = useCallback((allowanceOverride = null) => {
    if (isPreview) return;
    const authUrl = buildTrackedAuthRedirectUrl({
      cta_kind: (hasEverAuthenticated || hintAllowance?.eligible === false) ? 'sign_in' : 'sign_up',
      source_surface: 'game_hints',
      gate_reason: 'hints_requires_auth',
      next_path: `${location.pathname}${location.search || ''}${location.hash || ''}`,
      message: getHintAuthMessage(allowanceOverride?.game_slug === gameConfig.id ? allowanceOverride : hintAllowance),
      is_returning_device: hasEverAuthenticated,
      game_slug: gameConfig?.id || null,
      track_gate_viewed: true,
      trigger: 'hint_click_redirect',
    });
    navigate(authUrl);
  }, [gameConfig?.id, hasEverAuthenticated, isPreview, location.pathname, location.search, navigate, hintAllowance, location.hash]);

  const triggerInvalidFeedback = useCallback((path) => {
    const next = new Set(path.map(({ row, col }) => cellKey(row, col)));
    setInvalidCellKeys(next);

    if (invalidFeedbackTimeoutRef.current) {
      clearTimeout(invalidFeedbackTimeoutRef.current);
    }

    invalidFeedbackTimeoutRef.current = setTimeout(() => {
      setInvalidCellKeys(new Set());
    }, 450);
  }, []);

  const clearCurrentPath = useCallback(() => {
    setCurrentPath([]);
  }, []);

  const updatePathFromCell = useCallback((row, col) => {
    if (isInteractionDisabled) return;

    const key = cellKey(row, col);

    setCurrentPath((previousPath) => {
      if (lockedCellKeys.has(key)) {
        return previousPath;
      }

      if (previousPath.length === 0) {
        return [{ row, col }];
      }

      const last = previousPath[previousPath.length - 1];
      if (last.row === row && last.col === col) {
        return previousPath.slice(0, -1);
      }

      if (previousPath.length > 1) {
        const beforeLast = previousPath[previousPath.length - 2];
        if (beforeLast.row === row && beforeLast.col === col) {
          return previousPath.slice(0, -1);
        }
      }

      const existsInPath = previousPath.some((cell) => cell.row === row && cell.col === col);
      if (existsInPath) {
        return previousPath;
      }

      if (isAdjacent(last, { row, col })) {
        return [...previousPath, { row, col }];
      }

      // Non-adjacent free cell starts a new path.
      return [{ row, col }];
    });
  }, [isInteractionDisabled, lockedCellKeys]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 3 } }),
    useSensor(TouchSensor, { pressDelay: 250, pressTolerance: 12 }),
  );

  const collisionDetection = useCallback((args) => {
    const pointer = args.pointerCoordinates;
    if (!pointer) return closestCenter(args);

    const collisions = [];

    args.droppableContainers.forEach((container) => {
      const rect = args.droppableRects.get(container.id);
      if (!rect) return;

      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const radius = Math.max(Math.min(rect.width, rect.height) * CELL_HIT_RADIUS_RATIO, CELL_HIT_RADIUS_MIN_PX);
      const distance = Math.hypot(pointer.x - centerX, pointer.y - centerY);

      if (distance <= radius) {
        collisions.push({
          id: container.id,
          data: { droppableContainer: container, value: distance },
        });
      }
    });

    if (collisions.length === 0) return [];
    collisions.sort((a, b) => a.data.value - b.data.value);
    return collisions;
  }, []);

  const extractCellFromDndData = useCallback((data) => {
    const row = data?.row;
    const col = data?.col;
    const key = data?.key;
    if (!Number.isInteger(row) || !Number.isInteger(col) || typeof key !== 'string') return null;
    return { row, col, key };
  }, []);

  const handleDragStart = useCallback((event) => {
    if (isInteractionDisabled) return;

    const cell = extractCellFromDndData(event?.active?.data?.current);
    if (!cell) return;

    lastDragOverKeyRef.current = cell.key;
    if (currentPath.length === 0 || currentTipKey !== cell.key) {
      updatePathFromCell(cell.row, cell.col);
    }
  }, [currentPath.length, currentTipKey, extractCellFromDndData, isInteractionDisabled, updatePathFromCell]);

  const handleDragOver = useCallback((event) => {
    if (isInteractionDisabled) return;

    const cell = extractCellFromDndData(event?.over?.data?.current);
    if (!cell) return;
    if (lastDragOverKeyRef.current === cell.key) return;

    lastDragOverKeyRef.current = cell.key;
    updatePathFromCell(cell.row, cell.col);
  }, [extractCellFromDndData, isInteractionDisabled, updatePathFromCell]);

  const handleDragEnd = useCallback((event) => {
    suppressClickUntilRef.current = Date.now() + DRAG_CLICK_SUPPRESS_MS;

    const cell = extractCellFromDndData(event?.over?.data?.current);
    if (cell && lastDragOverKeyRef.current !== cell.key) {
      updatePathFromCell(cell.row, cell.col);
    }

    lastDragOverKeyRef.current = null;
  }, [extractCellFromDndData, updatePathFromCell]);

  const handleDragCancel = useCallback(() => {
    suppressClickUntilRef.current = Date.now() + DRAG_CLICK_SUPPRESS_MS;
    lastDragOverKeyRef.current = null;
  }, []);

  const handleCellClick = useCallback((row, col) => {
    if (Date.now() < suppressClickUntilRef.current) return;
    updatePathFromCell(row, col);
  }, [updatePathFromCell]);

  const handleSubmit = useCallback(async () => {
    if (isInteractionDisabled) return;

    if (currentPath.length === 0) {
      snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.NO_SELECTION, 'error');
      return;
    }

    const submittedPath = [...currentPath];
    const indexes = submittedPath.map(({ row, col }) => [row, col]);
    const text = buildTextFromPath(board, submittedPath);

    setIsSubmitting(true);

    try {
      const response = await dispatchEvent('submit', { indexes, text });

      if (!response.success) {
        if (response.error === 'GAME_OVER') {
          snackAlertRef.current?.showSnackAlert('Partita terminata.', 'error');
        } else if (response.error === 'STALE_STATE') {
          snackAlertRef.current?.showSnackAlert('Stato non aggiornato, riprova.', 'error');
        } else {
          snackAlertRef.current?.showSnackAlert(response.message || ERROR_MESSAGES.SUBMIT_ERROR, 'error');
        }
        return;
      }

      const result = response.result || {};

      if (!result.matched) {
        triggerInvalidFeedback(submittedPath);
        snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.INVALID_WORD, 'error');
        if (shouldTrack) {
          logGameEvent('guess_incorrect', gameConfig.id, effectiveDate);
        }
        setCurrentPath([]);
        return;
      }

      if (result.match_type === 'spangram') {
        if (!result.already_found) {
          setFoundSpangramPath(submittedPath);
          setHintedCellKeys((prev) => {
            const next = new Set(prev);
            submittedPath.forEach(({ row, col }) => next.delete(cellKey(row, col)));
            return next;
          });
          snackAlertRef.current?.showSnackAlert('Strisciogramma trovato!', 'success');
          if (shouldTrack) {
            logGameEvent('guess_correct', gameConfig.id, effectiveDate, { type: 'spangram' });
          }
        } else {
          snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.ALREADY_FOUND, 'error');
        }
      } else if (result.match_type === 'word') {
        if (!result.already_found && Number.isInteger(result.word_index)) {
          setFoundWordPathsByIndex((previous) => ({
            ...previous,
            [result.word_index]: submittedPath,
          }));
          setHintedCellKeys((prev) => {
            const next = new Set(prev);
            submittedPath.forEach(({ row, col }) => next.delete(cellKey(row, col)));
            return next;
          });
          snackAlertRef.current?.showSnackAlert('Parola tema trovata!', 'success');
          if (shouldTrack) {
            logGameEvent('guess_correct', gameConfig.id, effectiveDate, { type: 'word' });
          }
        } else {
          snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.ALREADY_FOUND, 'error');
        }
      }

      setCurrentPath([]);
    } catch (error) {
      console.error('Serpentoni submit error:', error);
      snackAlertRef.current?.showSnackAlert(ERROR_MESSAGES.SUBMIT_ERROR, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    board,
    currentPath,
    dispatchEvent,
    effectiveDate,
    gameConfig.id,
    isInteractionDisabled,
    shouldTrack,
    triggerInvalidFeedback,
  ]);

  const handleRevealWordIndexesHint = useCallback(async () => {
    if (gameOver) {
      return;
    }
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!hintAffordability.reveal_word_indexes) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_reveal_word_indexes',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: revealWordIndexesCost,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }

    setAiutiniError('');
    setLoadingHint('reveal_word_indexes');

    try {
      const response = await dispatchHintEvent('hint_reveal_word_indexes', {}, redirectToAuth);

      if (!response.success) {
        if (response.error === 'INSUFFICIENT_BALANCE') {
          setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
        } else if (response.error === 'NO_HINT_AVAILABLE') {
          setAiutiniError(ERROR_MESSAGES.HINT_NOT_AVAILABLE);
        } else if (response.error === 'NOT_AUTHENTICATED') {
          return;
        } else {
          setAiutiniError(response.message || ERROR_MESSAGES.SUBMIT_ERROR);
        }
        return;
      }

      const wordIndexes =
        response?.result?.word_indexes ||
        response?.hint_data?.word_indexes ||
        response?.result?.hint_data?.word_indexes ||
        [];
      if (Array.isArray(wordIndexes) && wordIndexes.length > 0) {
        setHintedCellKeys((previous) => {
          const next = new Set(previous);
          wordIndexes.forEach((pair) => {
            if (!Array.isArray(pair) || pair.length !== 2) return;
            const [row, col] = pair;
            if (!Number.isInteger(row) || !Number.isInteger(col)) return;
            next.add(cellKey(row, col));
          });
          return next;
        });

        snackAlertRef.current?.showSnackAlert('Aiutino usato!', 'success', 1600);
        closeModal();
      } else {
        setAiutiniError(ERROR_MESSAGES.HINT_NOT_AVAILABLE);
      }
    } catch (error) {
      console.error('Serpentoni hint error:', error);
      setAiutiniError(ERROR_MESSAGES.SUBMIT_ERROR);
    } finally {
      setLoadingHint(null);
    }
  }, [
    balance,
    closeModal,
    dispatchHintEvent,
    effectiveDate,
    gameConfig.id,
    gameOver,
    hintAffordability.reveal_word_indexes,
    isSignedIn,
    revealWordIndexesCost,
    redirectToAuth,
    shouldTrack,
    trackGameHintInsufficientBalance,
    hasFreeHints,
    isPreview,
  ]);

  const handleRevealWordToFindHint = useCallback(async () => {
    if (gameOver) {
      return;
    }
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!hintAffordability.reveal_word_to_find) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_reveal_word_to_find',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: revealWordToFindCost,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }

    setAiutiniError('');
    setLoadingHint('reveal_word_to_find');

    try {
      const response = await dispatchHintEvent('hint_reveal_word_to_find', {}, redirectToAuth);

      if (!response.success) {
        if (response.error === 'INSUFFICIENT_BALANCE') {
          setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
        } else if (response.error === 'NO_HINT_AVAILABLE') {
          setAiutiniError(ERROR_MESSAGES.HINT_NOT_AVAILABLE);
        } else if (response.error === 'NOT_AUTHENTICATED') {
          return;
        } else {
          setAiutiniError(response.message || ERROR_MESSAGES.SUBMIT_ERROR);
        }
        return;
      }

      const hintedWord =
        response?.result?.word_text ||
        response?.hint_data?.word_text ||
        response?.result?.hint_data?.word_text ||
        '';
      if (typeof hintedWord === 'string' && hintedWord.trim()) {
        snackAlertRef.current?.showSnackAlert('Aiutino usato!', 'success', 1600);
        closeModal();
      } else {
        setAiutiniError(ERROR_MESSAGES.HINT_NOT_AVAILABLE);
      }
    } catch (error) {
      console.error('Serpentoni hint error:', error);
      setAiutiniError(ERROR_MESSAGES.SUBMIT_ERROR);
    } finally {
      setLoadingHint(null);
    }
  }, [
    balance,
    closeModal,
    dispatchHintEvent,
    effectiveDate,
    gameConfig.id,
    gameOver,
    hintAffordability.reveal_word_to_find,
    isSignedIn,
    revealWordToFindCost,
    redirectToAuth,
    shouldTrack,
    trackGameHintInsufficientBalance,
    hasFreeHints,
    isPreview,
  ]);

  const handleRevealWordStartsHint = useCallback(async () => {
    if (gameOver) {
      return;
    }
    if (!isSignedIn && !isPreview && !hasFreeHints) {
      redirectToAuth();
      return;
    }
    if (!hintAffordability.reveal_word_starts) {
      if (shouldTrack) {
        trackGameHintInsufficientBalance({
          gameSlug: gameConfig.id,
          gameDate: effectiveDate,
          hintType: 'hint_reveal_word_starts',
          source: 'client_precheck',
          balanceBefore: balance,
          hintCost: revealWordStartsCost,
          modalName: 'aiutini',
        });
      }
      setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
      return;
    }

    setAiutiniError('');
    setLoadingHint('reveal_word_starts');

    try {
      const response = await dispatchHintEvent('hint_reveal_word_starts', {}, redirectToAuth);

      if (!response.success) {
        if (response.error === 'INSUFFICIENT_BALANCE') {
          setAiutiniError(INSUFFICIENT_HINT_BALANCE_MESSAGE);
        } else if (response.error === 'NO_HINT_AVAILABLE') {
          setAiutiniError(ERROR_MESSAGES.HINT_NOT_AVAILABLE);
        } else if (response.error === 'ALREADY_USED') {
          setAiutiniError(ERROR_MESSAGES.HINT_ALREADY_USED);
        } else if (response.error === 'NOT_AUTHENTICATED') {
          return;
        } else {
          setAiutiniError(response.message || ERROR_MESSAGES.SUBMIT_ERROR);
        }
        return;
      }

      const startIndexes =
        response?.result?.start_indexes ||
        response?.hint_data?.start_indexes ||
        response?.result?.hint_data?.start_indexes ||
        [];

      if (Array.isArray(startIndexes) && startIndexes.length > 0) {
        snackAlertRef.current?.showSnackAlert('Aiutino usato!', 'success', 1600);
        closeModal();
      } else {
        setAiutiniError(ERROR_MESSAGES.HINT_NOT_AVAILABLE);
      }
    } catch (error) {
      console.error('Serpentoni hint error:', error);
      setAiutiniError(ERROR_MESSAGES.SUBMIT_ERROR);
    } finally {
      setLoadingHint(null);
    }
  }, [
    balance,
    closeModal,
    dispatchHintEvent,
    effectiveDate,
    gameConfig.id,
    gameOver,
    hintAffordability.reveal_word_starts,
    isSignedIn,
    revealWordStartsCost,
    redirectToAuth,
    shouldTrack,
    trackGameHintInsufficientBalance,
    hasFreeHints,
    isPreview,
  ]);

  const handleRevealWordIndexesHintRef = useRef(handleRevealWordIndexesHint);
  const handleRevealWordToFindHintRef = useRef(handleRevealWordToFindHint);
  const handleRevealWordStartsHintRef = useRef(handleRevealWordStartsHint);
  const aiutiniModalSignatureRef = useRef('');

  useEffect(() => {
    handleRevealWordIndexesHintRef.current = handleRevealWordIndexesHint;
  }, [handleRevealWordIndexesHint]);

  useEffect(() => {
    handleRevealWordToFindHintRef.current = handleRevealWordToFindHint;
  }, [handleRevealWordToFindHint]);

  useEffect(() => {
    handleRevealWordStartsHintRef.current = handleRevealWordStartsHint;
  }, [handleRevealWordStartsHint]);

  useEffect(() => {
    if (!isAiutiniModalOpen) {
      aiutiniModalSignatureRef.current = '';
      setAiutiniError('');
      return;
    }

    const nextSignature = JSON.stringify({
      loadingHint: loadingHint || '',
      aiutiniError,
      disabled: !!gameOver,
      revealWordStartsUsed,
      revealWordToFindCost: aiutiniHintCosts.reveal_word_to_find ?? null,
      revealWordIndexesCost: aiutiniHintCosts.reveal_word_indexes ?? null,
      revealWordStartsCost: aiutiniHintCosts.reveal_word_starts ?? null,
      revealWordToFindAffordable: !!hintAffordability.reveal_word_to_find,
      revealWordIndexesAffordable: !!hintAffordability.reveal_word_indexes,
      revealWordStartsAffordable: !!hintAffordability.reveal_word_starts,
      isSignedIn,
      hintAllowance,
      hasFreeHints,
      hasEverAuthenticated,
    });

    if (aiutiniModalSignatureRef.current === nextSignature) {
      return;
    }
    aiutiniModalSignatureRef.current = nextSignature;

    refreshAiutiniModal(
      <Aiutini
        onRevealWordToFind={() => handleRevealWordToFindHintRef.current?.()}
        onReveal={() => handleRevealWordIndexesHintRef.current?.()}
        onRevealStarts={() => handleRevealWordStartsHintRef.current?.()}
        revealWordStartsUsed={revealWordStartsUsed}
        disabled={gameOver}
        loadingHint={loadingHint}
        hintCosts={aiutiniHintCosts}
        hintAffordability={hintAffordability}
        errorMessage={aiutiniError}
        isSignedIn={isSignedIn}
        hasFreeHints={hasFreeHints}
        onAuthRedirect={redirectToAuth}
        hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
        isPreview={isPreview}
        balance={balance}
      />,
      'Aiutini',
    );
  }, [
    aiutiniHintCosts,
    hasEverAuthenticated,
    hintAffordability,
    gameOver,
    isAiutiniModalOpen,
    isSignedIn,
    isPreview,
    aiutiniError,
    loadingHint,
    revealWordStartsUsed,
    refreshAiutiniModal,
    redirectToAuth,
    hasFreeHints,
    hintAllowance,
  ]);

  const cellCentersByKey = useMemo(() => {
    if (!boardRef.current) return {};

    const boardRect = boardRef.current.getBoundingClientRect();
    const next = {};

    cellRefs.current.forEach((element, key) => {
      const rect = element.getBoundingClientRect();
      next[key] = {
        x: rect.left - boardRect.left + rect.width / 2,
        y: rect.top - boardRect.top + rect.height / 2,
      };
    });

    return next;
  }, [layoutVersion]);

  const foundWordSegments = useMemo(
    () => effectiveWordPaths.flatMap((path) => buildPathSegments(path, cellCentersByKey)),
    [cellCentersByKey, effectiveWordPaths],
  );

  const spangramSegments = useMemo(
    () => buildPathSegments(effectiveSpangramPath, cellCentersByKey),
    [cellCentersByKey, effectiveSpangramPath],
  );

  const activePathSegments = useMemo(
    () => buildPathSegments(currentPath, cellCentersByKey),
    [cellCentersByKey, currentPath],
  );

  const aiMetadataByPathKey = useMemo(() => {
    if (!aiSelectionEnabled) return {};

    const metadataBySolutionPathKey = new Map();

    (solutionBundle?.words || []).forEach((entry) => {
      if (!entry?.text) return;
      const path = normalizePathFromIndexes(entry.indexes);
      if (path.length === 0) return;
      metadataBySolutionPathKey.set(serializePath(path), {
        component: 'serpentoni_word',
        word: String(entry.text).toLowerCase(),
      });
    });

    if (solutionBundle?.spangram?.text) {
      const spangramPath = normalizePathFromIndexes(solutionBundle.spangram.indexes);
      if (spangramPath.length > 0) {
        metadataBySolutionPathKey.set(serializePath(spangramPath), {
          component: 'serpentoni_spangram',
          word: String(solutionBundle.spangram.text).toLowerCase(),
        });
      }
    }

    const resolveMetadata = (path, component) => {
      const key = serializePath(path);
      const metadataFromSolution = metadataBySolutionPathKey.get(key);
      if (metadataFromSolution?.word) return metadataFromSolution;

      const fallbackWord = buildTextFromPath(board, path).toLowerCase();
      if (!fallbackWord) return null;

      return { component, word: fallbackWord };
    };

    const next = {};

    effectiveWordPaths.forEach((path) => {
      const key = serializePath(path);
      if (!key) return;
      const metadata = resolveMetadata(path, 'serpentoni_word');
      if (!metadata?.word) return;
      next[key] = metadata;
    });

    if (effectiveSpangramPath.length > 0) {
      const key = serializePath(effectiveSpangramPath);
      if (key) {
        const metadata = resolveMetadata(effectiveSpangramPath, 'serpentoni_spangram');
        if (metadata?.word) next[key] = metadata;
      }
    }

    return next;
  }, [aiSelectionEnabled, board, effectiveSpangramPath, effectiveWordPaths, solutionBundle]);

  const aiMetadataByCellKey = useMemo(() => {
    if (!aiSelectionEnabled) return {};

    const next = {};

    effectiveWordPaths.forEach((path) => {
      const metadata = aiMetadataByPathKey[serializePath(path)];
      if (!metadata?.word) return;
      path.forEach(({ row, col }) => {
        next[cellKey(row, col)] = metadata;
      });
    });

    if (effectiveSpangramPath.length > 0) {
      const metadata = aiMetadataByPathKey[serializePath(effectiveSpangramPath)];
      if (metadata?.word) {
        effectiveSpangramPath.forEach(({ row, col }) => {
          next[cellKey(row, col)] = metadata;
        });
      }
    }

    return next;
  }, [aiMetadataByPathKey, aiSelectionEnabled, effectiveSpangramPath, effectiveWordPaths]);

  const selectedAiPathSegments = useMemo(() => {
    if (!aiSelectionEnabled || !selectedAiLookupKey) return [];

    const selectedPaths = [];

    effectiveWordPaths.forEach((path) => {
      const metadata = aiMetadataByPathKey[serializePath(path)];
      if (!metadata?.word) return;
      const lookupKey = getAiLookupKey(metadata.component, metadata.word);
      if (lookupKey === selectedAiLookupKey) {
        selectedPaths.push(path);
      }
    });

    if (effectiveSpangramPath.length > 0) {
      const metadata = aiMetadataByPathKey[serializePath(effectiveSpangramPath)];
      if (metadata?.word) {
        const lookupKey = getAiLookupKey(metadata.component, metadata.word);
        if (lookupKey === selectedAiLookupKey) {
          selectedPaths.push(effectiveSpangramPath);
        }
      }
    }

    return selectedPaths.flatMap((path, pathIndex) => (
      buildPathSegments(path, cellCentersByKey).map((segment, segmentIndex) => ({
        ...segment,
        key: `selected-${pathIndex}-${segmentIndex}-${segment.key}`,
      }))
    ));
  }, [
    aiMetadataByPathKey,
    aiSelectionEnabled,
    cellCentersByKey,
    effectiveSpangramPath,
    effectiveWordPaths,
    selectedAiLookupKey,
  ]);

  useEffect(() => {
    if (!aiSelectionEnabled) {
      setHoveredAiLookupKey('');
    }
  }, [aiSelectionEnabled]);

  const handleClosePreview = useCallback(() => {
    window.close();
  }, []);

  const isGameplayAdActive = !gameOver;
  const mobileGameplayAd = useMemo(() => ({
    enabled: isGameplayAdActive || !!endSectionAnchor,
    anchors: endSectionAnchor ? [endSectionAnchor] : [gameplayControlsRef],
    placement: `game-shell-${gameConfig.id}-mobile-gameplay-banner`,
    testId: `${gameConfig.id}-mobile-gameplay-banner`,
  }), [endSectionAnchor, gameConfig.id, isGameplayAdActive]);

  const headerActions = [
    {
      icon: 'fa fa-question-circle',
      title: 'Istruzioni',
      onClick: () => {
        if (shouldTrack) {
          logGameEvent('instructions_viewed', gameConfig.id, effectiveDate, { first_time: false });
        }
        showModal({ title: 'Istruzioni', content: <Instructions /> });
      },
    },
    {
      icon: 'fa fa-medkit',
      title: 'Aiutini',
      onClick: () => {
        setAiutiniError('');
        if (shouldTrack) {
          logGameEvent('help_viewed', gameConfig.id, effectiveDate);
        }
        showAiutiniModal(
          <Aiutini
            onRevealWordToFind={handleRevealWordToFindHint}
            onReveal={handleRevealWordIndexesHint}
            onRevealStarts={handleRevealWordStartsHint}
            revealWordStartsUsed={revealWordStartsUsed}
            disabled={gameOver}
            loadingHint={loadingHint}
            hintCosts={aiutiniHintCosts}
            hintAffordability={hintAffordability}
            errorMessage=""
            isSignedIn={isSignedIn}
            hasFreeHints={hasFreeHints}
            onAuthRedirect={redirectToAuth}
            hasEverAuthenticated={hasEverAuthenticated || hintAllowance?.eligible === false}
            isPreview={isPreview}
            balance={balance}
          />,
          'Aiutini',
        );
      },
    },
  ];

  if (canOpenSolutionsModal) {
    headerActions.push({
      icon: 'fa fa-check-circle',
      title: 'Soluzioni',
      onClick: () => {
        if (shouldTrack) {
          logGameEvent('answers_viewed', gameConfig.id, effectiveDate);
        }
        showModal({
          title: 'Soluzioni',
          content: (
            <SerpentoniSolutionsModal
              prompt={solutionsPrompt}
              spangramText={solutionsSpangramText}
              themeWords={solutionsThemeWords}
            />
          ),
        });
      },
    });
  }

  if (isLoading || !puzzleConfig) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
      >
        <div className={styles.container}>
          <p className={gameStyles.greeting}>{getGameMessage(gameConfig.id, 'not_started')}</p>
          <SerpentoniSkeleton />
        </div>
      </GameShell>
    );
  }

  if (sessionError && !sessionState) {
    return (
      <GameShell
        gameConfig={gameConfig}
        actions={headerActions}
        sponsorship={sponsorship}
        isModalOpen={isModalOpen}
        onModalClose={closeModal}
        modalTitle={modalTitle}
        modalContent={modalContent}
        showNavigation={showNavigation}
        showGameHeader={showGameHeader}
      >
        <div className={styles.container}>
          <p className={gameStyles.greeting}>{ERROR_MESSAGES.GAME_LOAD_ERROR}</p>
        </div>
      </GameShell>
    );
  }

  return (
    <GameShell
      gameConfig={gameConfig}
      actions={headerActions}
      sponsorship={sponsorship}
      isModalOpen={isModalOpen}
      onModalClose={closeModal}
      modalTitle={modalTitle}
      modalContent={modalContent}
      key={sessionState?.session_id || effectiveDate}
      resultsPresentationReady={showEndContent && isLossRevealComplete}
      showCelebration={justWon}
      isCompleted={gameOver}
      showNavigation={showNavigation}
      showGameHeader={showGameHeader}
      mobileGameplayAd={mobileGameplayAd}
    >
      <SnackAlert ref={snackAlertRef} />

      <AIHelperTrigger
        puzzleInstanceId={puzzleInstanceId}
        gameSlug={gameConfig.id}
        enabled={aiSelectionEnabled}
        onboardingReady={isAIOnboardingReady}
        coachmarkSelector='[data-ai-component="serpentoni_word"], [data-ai-component="serpentoni_spangram"]'
      >
        <div className={styles.container} style={{ '--board-max-width': boardMaxWidth }}>
          <div className={styles.topIntro}>
            {!gameOver && (
              <p className={`${gameStyles.greeting} ${styles.greetingText}`}>
                {getGameMessage(gameConfig.id, 'not_started')}
              </p>
            )}
            {puzzleConfig?.prompt && (
              <div className={styles.promptStack}>
                <div className={`${styles.promptSplitRow} ${styles.promptSplitRowTop}`}>
                  <div className={`${styles.promptSplitLead} ${styles.promptSplitLeadPrimary}`}>
                    <FaIcon className={`fa-solid fa-compass ${styles.promptSplitLeadIcon}`} aria-label="Tema" title="Tema" />
                  </div>
                  <div className={styles.promptSplitText}>
                    <b data-ai-component="title">{puzzleConfig.prompt}</b>
                  </div>
                </div>

                <div className={`${styles.promptSplitRow} ${styles.promptSplitRowBottom}`}>
                  <div className={`${styles.promptSplitLead} ${styles.promptSplitLeadSecondary}`}>
                    <FaIcon className={`fa-solid fa-gauge-high ${styles.promptSplitLeadIcon}`} aria-label="Progresso parole tema" title="Progresso parole tema" />
                  </div>
                  <div className={styles.promptSplitText}>
                    <span>Hai trovato</span>
                    <b>{foundThemeWords}</b>
                    <span>delle</span>
                    <b>{themeWordsTotal}</b>
                    <span>parole tema.</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.currentSelection} aria-live="polite">
            {lossRevealCurrentText || (currentSelectionChars ? (
              <span className={styles.currentSelectionChars}>
                {currentSelectionChars.map((char, index) => (
                  <span
                    key={`${char.value}-${index}`}
                    className={char.isHint ? styles.currentSelectionHintChar : styles.currentSelectionTypedChar}
                  >
                    {char.value}
                  </span>
                ))}
              </span>
            ) : (currentPathText || '\u00A0'))}
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <div className={styles.boardTouchGutter}>
              <div
                ref={boardRef}
                className={`${styles.board} ${invalidCellKeys.size > 0 ? styles.boardShake : ''}`}
                style={{
                  gridTemplateColumns: `repeat(${boardWidth}, minmax(0, 1fr))`,
                  gridTemplateRows: `repeat(${boardHeight}, minmax(0, 1fr))`,
                }}
              >
                {(foundWordSegments.length > 0
                  || spangramSegments.length > 0
                  || activePathSegments.length > 0
                  || selectedAiPathSegments.length > 0)
                  && boardMetrics.width > 0
                  && boardMetrics.height > 0 && (
                  <svg
                    className={styles.pathOverlay}
                    viewBox={`0 0 ${boardMetrics.width} ${boardMetrics.height}`}
                    preserveAspectRatio="none"
                    aria-hidden="true"
                  >
                    {foundWordSegments.map((segment) => (
                      <line
                        key={`word-${segment.key}`}
                        className={`${styles.pathSegment} ${styles.wordPathSegment}`}
                        x1={segment.x1}
                        y1={segment.y1}
                        x2={segment.x2}
                        y2={segment.y2}
                      />
                    ))}
                    {spangramSegments.map((segment) => (
                      <line
                        key={`spangram-${segment.key}`}
                        className={`${styles.pathSegment} ${styles.spangramPathSegment}`}
                        x1={segment.x1}
                        y1={segment.y1}
                        x2={segment.x2}
                        y2={segment.y2}
                      />
                    ))}
                    {activePathSegments.map((segment) => (
                      <line
                        key={`active-${segment.key}`}
                        className={`${styles.pathSegment} ${styles.activePathSegment}`}
                        x1={segment.x1}
                        y1={segment.y1}
                        x2={segment.x2}
                        y2={segment.y2}
                      />
                    ))}
                    {selectedAiPathSegments.map((segment) => (
                      <line
                        key={segment.key}
                        className={`${styles.pathSegment} ${styles.selectedAiPathSegment}`}
                        x1={segment.x1}
                        y1={segment.y1}
                        x2={segment.x2}
                        y2={segment.y2}
                      />
                    ))}
                  </svg>
                )}

                {board.map((rowLetters, rowIndex) => rowLetters.map((letter, colIndex) => {
                  const key = cellKey(rowIndex, colIndex);

                  const isInPath = currentPathKeys.has(key);
                  const isInvalid = invalidCellKeys.has(key);
                  const isSpangramCell = spangramCellKeys.has(key);
                  const isFoundWordCell = foundWordCellKeys.has(key);
                  const isStartHintedCell = (
                    revealedWordStartCellKeys.has(key)
                    && !isSpangramCell
                    && !isFoundWordCell
                    && !isInPath
                  );
                  const isHintedCell = hintedCellKeys.has(key) && !isSpangramCell && !isFoundWordCell && !isInPath;
                  const isLockedCell = lockedCellKeys.has(key);
                  const isBlockedByCurrentPath = isInPath && key !== backtrackTargetKey && key !== currentTipKey;
                  const isBlockedCell = isLockedCell || isBlockedByCurrentPath;
                  const aiMetadata = aiMetadataByCellKey[key];
                  const aiLookupKey = getAiLookupKey(aiMetadata?.component, aiMetadata?.word);
                  const isAiSelectable = aiSelectionEnabled && !!aiLookupKey;
                  const isAiWordHovered = isAiSelectable && aiLookupKey === hoveredAiLookupKey;
                  const isAiWordSelected = isAiSelectable && aiLookupKey === selectedAiLookupKey;

                  return (
                    <SerpentoniCell
                      key={key}
                      cellId={key}
                      row={rowIndex}
                      col={colIndex}
                      letter={letter}
                      setCellRef={setCellRef}
                      className={[
                        styles.cell,
                        isInPath && styles.cellActive,
                        isFoundWordCell && styles.cellFoundWord,
                        isSpangramCell && styles.cellSpangram,
                        isHintedCell && styles.cellHinted,
                        isStartHintedCell && styles.cellStartHinted,
                        isHintedCell && isStartHintedCell && styles.cellStartHintedWithLetterHint,
                        isInvalid && styles.cellInvalid,
                        isBlockedCell && styles.cellBlocked,
                        isAiSelectable && styles.cellAiSelectable,
                        isAiWordHovered && styles.cellWordHovered,
                        isAiWordSelected && styles.cellWordSelected,
                      ].filter(Boolean).join(' ')}
                      isInteractionDisabled={isInteractionDisabled}
                      isDragDisabled={isInteractionDisabled || isBlockedCell}
                      allowAiSelection={isAiSelectable}
                      aiComponent={aiMetadata?.component}
                      aiWord={aiMetadata?.word}
                      onHoverStart={isAiSelectable ? () => setHoveredAiLookupKey(aiLookupKey) : undefined}
                      onHoverEnd={isAiSelectable ? () => setHoveredAiLookupKey('') : undefined}
                      isPressed={isInPath}
                      onClick={() => handleCellClick(rowIndex, colIndex)}
                    />
                  );
                }))}
              </div>
            </div>
          </DndContext>

          {!gameOver && (
            <div className={styles.bottomContainer} ref={gameplayControlsRef}>
              <div className={styles.livesRow}>
                <LivesDisplay lives={lives} maxLives={MAX_LIVES} className={styles.livesDisplay} />
              </div>

              <div className={styles.actionsRow}>
                <Button
                  secondary
                  onClick={clearCurrentPath}
                  disabled={isInteractionDisabled || currentPath.length === 0}
                >
                  Annulla
                </Button>
                <Button
                  primary
                  onClick={handleSubmit}
                  disabled={isInteractionDisabled || currentPath.length === 0}
                  loading={isSubmitPending}
                  loadingStyle="spinner"
                >
                  Invia
                </Button>
              </div>
            </div>
          )}

          {gameOver && showEndContent && isLossRevealComplete && (
            isPreview && !previewSponsorship ? (
              <div ref={setEndSectionAnchor} className={styles.previewEndSection}>
                <Button primary onClick={handleClosePreview}>
                  {previewEndLabel || 'Chiudi anteprima'}
                </Button>
              </div>
            ) : (
              <GameEndSection
                ref={setEndSectionAnchor}
                won={won}
                justWon={justWon}
                gameConfig={gameConfig}
                sessionState={sessionState}
                puzzleConfig={puzzleConfig}
                justCompleted={justWon || justLost}
                date={effectiveDate}
                shouldTrack={shouldTrack}
                sponsorship={sponsorship}
                previewMode={isPreview}
              />
            )
          )}
        </div>
      </AIHelperTrigger>
    </GameShell>
  );
};

export default SerpentoniGame;
